import { useState } from "react";
import { Button } from "@/design-system/components/ui";
import type { PortalItemDto } from "../api/types";
import { useLetterPreview } from "../hooks/useLetterPreview";
import {
  ApprovalChainProgress,
  ChainDoneCard,
  ChainReturnDialog,
} from "../components/ApprovalChainPanels";
import { InHouseSigningSurface } from "./InHouseSigningSurface";
import { letterSignerApi, type LetterSignerCheckDto } from "../api/letterSigner";
import { SignerMismatchDialog } from "../components/SignerMismatchDialog";

// Approval chains (D8) — one party's view of a chain item signed in-house.
// The page shows the CURRENT composed PDF (letter-preview serves it for a
// chain item), the stage strip, and actions driven ONLY by the caller's chain
// view: `canAct` → "Continue to endorse" / "Continue to sign" into the
// existing in-house consent + signature flow (the server completes the
// caller's stage); `canReturn` → "Return with comments". A chain item never
// uses the review-loop note, the Respond step, "Edit answers" or the AI
// checkpoint. Shared by every account type (skill rule 18): nothing here
// branches on the portal kind — the broker endorses because the server says
// their action is Endorse.

interface Props {
  item: PortalItemDto;
  onUpdated: (next: PortalItemDto) => void;
  /** Re-reads the item after the caller's endorsement / signature lands. */
  onReload?: () => void | Promise<void>;
  onSigningBusyChange?: (busy: boolean) => void;
}

export function ApprovalChainItemSurface({ item, onUpdated, onReload, onSigningBusyChange }: Props) {
  const chain = item.approvalChain;
  const [signing, setSigning] = useState(false);
  const [returnOpen, setReturnOpen] = useState(false);
  const [returned, setReturned] = useState(false);
  // Owner Recapitalization (SignerMismatchWarning) — the signer check runs when
  // the caller continues to sign; a mismatch on a recap letter warns first.
  const [checkingSigner, setCheckingSigner] = useState(false);
  const [signerWarning, setSignerWarning] = useState<LetterSignerCheckDto | null>(null);
  const { url, error, loading } = useLetterPreview(item.id, !!chain && !signing);

  if (!chain) return null;

  const endorse = chain.myAction === "Endorse";

  async function continueToSign() {
    if (endorse) {
      setSigning(true);
      return;
    }
    setCheckingSigner(true);
    try {
      const check = await letterSignerApi.check(item.id);
      if (check.show) {
        setSignerWarning(check);
        return;
      }
    } catch {
      // A warning only: a failed check never stands between the signer and
      // their signature.
    } finally {
      setCheckingSigner(false);
    }
    setSigning(true);
  }

  if (signing) {
    return (
      <InHouseSigningSurface
        item={item}
        onUpdated={(next) => {
          // Back to the review view: it now reads Done (the "recorded" card,
          // and the download once every party has signed), then the re-read
          // brings every other stage's state from the server.
          setSigning(false);
          onUpdated(next);
          void onReload?.();
        }}
        onBusyChange={onSigningBusyChange}
      />
    );
  }

  return (
    <div className="flex flex-col gap-space-4">
      <div className="bg-white-pure rounded-lg border border-border-light p-space-4 flex flex-col gap-space-3">
        <div className="flex items-center justify-between gap-space-2 flex-wrap">
          <h2 className="text-lead font-display text-onyx m-0">The document</h2>
          {url && (
            <div className="flex items-center gap-space-3">
              <a
                href={url}
                download={`${item.templateName || "document"}.pdf`}
                className="text-caption text-gold underline"
              >
                Download
              </a>
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-caption text-gold underline"
              >
                Open / print
              </a>
            </div>
          )}
        </div>
        {error ? (
          <p className="text-small text-status-error m-0" role="alert">
            We couldn't load the document right now. Please refresh and try again.
          </p>
        ) : url ? (
          <iframe
            src={url}
            title={`Document — ${item.templateName}`}
            className="w-full rounded-md border border-border-light bg-white-warm"
            style={{ height: "min(720px, 75dvh)" }}
          />
        ) : (
          <p className="text-small text-gray-mid italic m-0">
            {loading ? "Loading the document…" : "Preparing the document…"}
          </p>
        )}
      </div>

      <ApprovalChainProgress chain={chain} />

      {returned ? (
        <div
          className="flex flex-col gap-space-2 p-space-4 border border-border-light rounded-md bg-white-pure"
          data-testid="chain-returned"
        >
          <h2 className="text-body font-display font-semibold text-onyx m-0">Returned with comments</h2>
          <p className="text-small text-onyx m-0">
            Your comments were sent to Phoenix. The document stops here until it is revised.
          </p>
        </div>
      ) : chain.myStatus === "Done" ? (
        <ChainDoneCard item={item} />
      ) : chain.canAct || chain.canReturn ? (
        <div className="flex flex-col gap-space-2">
          <p className="text-small text-gray-mid m-0" data-testid="chain-status-label">
            {chain.statusLabel}
          </p>
          <div className="flex flex-wrap justify-end gap-space-2">
            {chain.canReturn && (
              <Button
                variant="secondary"
                onClick={() => setReturnOpen(true)}
                data-testid="chain-return-open"
              >
                Return with comments
              </Button>
            )}
            {chain.canAct && (
              <Button
                onClick={() => void continueToSign()}
                disabled={checkingSigner}
                data-testid="chain-continue"
              >
                {endorse ? "Continue to endorse" : "Continue to sign"}
              </Button>
            )}
          </div>
        </div>
      ) : (
        // Waiting (or a stopped chain): say whose turn it is; offer nothing.
        <p
          className="text-small text-onyx m-0 p-space-4 border border-border-light rounded-md bg-white-pure"
          data-testid="chain-waiting"
        >
          {chain.statusLabel}
        </p>
      )}

      {signerWarning && (
        <SignerMismatchDialog
          check={signerWarning}
          onReturn={() => {
            setSignerWarning(null);
            setReturnOpen(true);
          }}
          onContinue={() => {
            setSignerWarning(null);
            setSigning(true);
          }}
        />
      )}

      {returnOpen && (
        <ChainReturnDialog
          itemId={item.id}
          onClose={() => setReturnOpen(false)}
          onReturned={(next) => {
            setReturnOpen(false);
            setReturned(true);
            onUpdated(next);
          }}
        />
      )}
    </div>
  );
}
